import React, { useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Form from '../../Form';
import Modal from '../../Modal';
import {
    DEFAULT_LINE_ITEM,
    InvoiceFormSection,
    InvoiceLineItemsEditor,
    InvoiceTotalsSummary,
    centsToDollars,
    dollarsToCents,
    normalizeLineItems,
} from './InvoiceFormParts';

function EditInvoiceModal({ invoice, onClose, onSuccess }) {
    const { fetch: authFetch, API_URL } = useAuth();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const canEditLineItems = invoice.status === 'draft';
    const [values, setValues] = useState(() => ({
        invoiceNumber: invoice.invoiceNumber || '',
        dueAt: invoice.dueAt ? new Date(invoice.dueAt).toISOString().split('T')[0] : '',
        lineItems: invoice.lineItems?.length > 0
            ? normalizeLineItems(invoice.lineItems)
            : [{ ...DEFAULT_LINE_ITEM }],
        taxDollars: centsToDollars(invoice.taxCents),
        shippingDollars: centsToDollars(invoice.shippingCents),
        discountDollars: centsToDollars(invoice.discountCents),
    }));

    const lineItems = values.lineItems || [];
    const taxCents = dollarsToCents(values.taxDollars);
    const shippingCents = dollarsToCents(values.shippingDollars);
    const discountCents = dollarsToCents(values.discountDollars);
    const subtotalCents = lineItems.reduce((sum, item) => (
        sum + (Number.parseInt(item.quantity, 10) || 0) * (Number.parseInt(item.unitPriceCents, 10) || 0)
    ), 0);
    const totalCents = Math.max(0, subtotalCents + taxCents + shippingCents - discountCents);

    const handleSubmit = async (submittedValues) => {
        setError(null);
        setSaving(true);

        try {
            const payload = {
                invoiceNumber: submittedValues.invoiceNumber.trim() || null,
                dueAt: submittedValues.dueAt || null,
            };

            if (canEditLineItems) {
                payload.lineItems = normalizeLineItems(submittedValues.lineItems);
                payload.taxCents = dollarsToCents(submittedValues.taxDollars);
                payload.shippingCents = dollarsToCents(submittedValues.shippingDollars);
                payload.discountCents = dollarsToCents(submittedValues.discountDollars);
            }

            const response = await authFetch(
                `${API_URL}/v1/seller/invoices/${invoice.uuid}`,
                { method: 'PUT', body: JSON.stringify(payload) }
            );

            const json = await response.json();
            if (!response.ok) {
                setError(json.error || 'Failed to update invoice');
                return;
            }

            onSuccess(json.data);
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            onClose={onClose}
            title="Edit Invoice"
            isOpen={true}
            size="large"
            className="invoice-modal"
        >
            <Form
                values={values}
                onChange={setValues}
                onSubmit={handleSubmit}
                className="invoice-form"
                fields={[
                    {
                        name: 'invoiceBasics',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection
                                title="Invoice details"
                                description="Invoice number and due date can be updated while the invoice is editable."
                            >
                                <div className="invoice-form-grid invoice-form-grid--two">
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'invoiceNumber',
                                                label: 'Invoice number',
                                                maxLength: 100,
                                            },
                                            {
                                                name: 'dueAt',
                                                type: 'date',
                                                label: 'Due date',
                                            },
                                        ]}
                                    />
                                </div>
                            </InvoiceFormSection>
                        ),
                    },
                    canEditLineItems && {
                        name: 'lineItems',
                        type: 'render',
                        render: ({ value, onChange }) => (
                            <InvoiceFormSection title="Line items" description="Draft invoices can still change pricing and item details.">
                                <InvoiceLineItemsEditor
                                    lineItems={value}
                                    currency={invoice.currency}
                                    onChange={(nextItems) => onChange('lineItems', nextItems)}
                                />
                            </InvoiceFormSection>
                        ),
                    },
                    canEditLineItems && {
                        name: 'adjustments',
                        type: 'render',
                        render: ({ values: formValues, onChange }) => (
                            <InvoiceFormSection title="Adjustments">
                                <div className="invoice-form-grid invoice-form-grid--three">
                                    <Form
                                        as="div"
                                        values={formValues}
                                        onChange={(nextValues, name, value) => onChange(name, value)}
                                        className="invoice-subform"
                                        fields={[
                                            {
                                                name: 'taxDollars',
                                                type: 'number',
                                                label: 'Tax',
                                                min: 0,
                                                step: '0.01',
                                            },
                                            {
                                                name: 'shippingDollars',
                                                type: 'number',
                                                label: 'Shipping',
                                                min: 0,
                                                step: '0.01',
                                            },
                                            {
                                                name: 'discountDollars',
                                                type: 'number',
                                                label: 'Discount',
                                                min: 0,
                                                step: '0.01',
                                            },
                                        ]}
                                    />
                                </div>
                            </InvoiceFormSection>
                        ),
                    },
                ].filter(Boolean)}
            >
                {error && <div className="error invoice-form-alert">{error}</div>}

                {!canEditLineItems && (
                    <div className="info invoice-form-alert">
                        This invoice is <strong>{invoice.status}</strong>. Line items and amounts cannot be changed,
                        but you can update the invoice number and due date.
                    </div>
                )}

                {canEditLineItems && (
                    <InvoiceTotalsSummary
                        subtotalCents={subtotalCents}
                        taxCents={taxCents}
                        shippingCents={shippingCents}
                        discountCents={discountCents}
                        totalCents={totalCents}
                        currency={invoice.currency}
                    />
                )}

                <div className="form-actions invoice-form-actions">
                    <button type="button" className="button secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="button primary" disabled={saving}>
                        {saving ? 'Saving...' : 'Save changes'}
                    </button>
                </div>
            </Form>
        </Modal>
    );
}

export default EditInvoiceModal;
